import type { UnitType } from '@racemap/utilities/types/units';
import { FONT_FAMILY, FONT_SIZE, FONT_STYLE, SHADOW_TRACK_STEP } from 'src/lib/player/constants';
import { memoizedAscent, memoizedDescent } from 'src/lib/player/point_utils';
import type { XY } from 'src/lib/player/store';
import { formatDistance, formatElevation, NBSP } from 'src/lib/player/utils';
import type TrackView from 'src/lib/player/utils/TrackView';
import tinycolor from 'tinycolor2';
import type { Bounds } from '../InfoSheet/SVGChart';
import {
  getInterpolatedElevation,
  getInterpolatedStatistic,
  getInterpolatedY,
  isDataXInView,
  projectX,
  type ViewRange,
} from './heightChartUtils';

export function HeightChartCrosshair({
  bounds,
  unitType,
  highlightedOffset,
  track,
  filteredTrack,
  viewRange,
  lineColor,
}: {
  bounds: Bounds;
  unitType: UnitType;
  highlightedOffset: number;
  track: TrackView;
  filteredTrack: Array<XY>;
  viewRange: ViewRange;
  lineColor: string;
}) {
  const { minY, maxY } = viewRange;
  const trackOffset = Math.floor(highlightedOffset / SHADOW_TRACK_STEP);
  const hoverX = projectX(highlightedOffset, viewRange, bounds);
  const lineColorMuted = tinycolor(lineColor).setAlpha(0.4).toRgbString();

  const maxDistance = track.size() * SHADOW_TRACK_STEP;
  const distance = highlightedOffset;
  const elevation = getInterpolatedElevation(filteredTrack, highlightedOffset);

  const elevationLabel = formatElevation(elevation, unitType);
  const distanceLabel = formatDistance(distance, unitType);

  const accumulatedAscent = getInterpolatedStatistic(memoizedAscent(track), trackOffset);
  const accumulatedDescent = getInterpolatedStatistic(memoizedDescent(track), trackOffset);
  const ascentLabel = formatElevation(accumulatedAscent, unitType);
  const descentLabel = formatElevation(accumulatedDescent, unitType);

  const ascentLabelWidth =
    Math.max(ascentLabel.split(NBSP)[0].length, descentLabel.split(NBSP)[0].length) * 7 + 35;

  const posY = getInterpolatedY(elevation, minY, maxY, bounds);

  if (distance < 0 || distance > maxDistance) return null;
  if (!isDataXInView(highlightedOffset, viewRange)) return null;

  return (
    <g pointerEvents="none" className="notranslate">
      <g transform={`translate(${hoverX - ascentLabelWidth} 1)`}>
        <path
          fill={lineColorMuted}
          d="M12,0.5C12,0.8,11.8,1,11.5,1H0.5C0.2,1,0,0.8,0,0.5S0.2,0,0.5,0h11.1C11.8,0,12,0.2,12,0.5z"
        />
        <polygon
          fill={lineColor}
          points="2.9,3.3 3.5,3.8 5.5,1.6 5.5,12 6.5,12 6.5,1.6 8.5,3.8 9.1,3.3 6,0 	"
        />
      </g>
      <text
        x={hoverX - 4}
        y={11}
        style={{
          fontWeight: FONT_STYLE,
          fontFamily: FONT_FAMILY,
          fontSize: FONT_SIZE,
          textAlign: 'right',
          textAnchor: 'end',
          fill: lineColor,
        }}
      >
        {ascentLabel}
      </text>
      <g transform={`translate(${hoverX - ascentLabelWidth} 16)`}>
        <path
          d="M12,0.5c0,0.3 -0.2,0.5 -0.5,0.5l-11,0c-0.3,0 -0.5,-0.2 -0.5,-0.5c0,-0.3 0.2,-0.5 0.5,-0.5l11.1,0c0.2,0 0.4,0.2 0.4,0.5Z"
          fill={lineColorMuted}
        />
        <path
          d="M2.9,8.7l0.6,-0.5l2,2.2l0,-10.4l1,0l0,10.4l2,-2.2l0.6,0.5l-3.1,3.3l-3.1,-3.3Z"
          fill={lineColor}
        />
      </g>
      <text
        x={hoverX - 4}
        y={0 + 11 + 15}
        style={{
          fontWeight: FONT_STYLE,
          fontFamily: FONT_FAMILY,
          fontSize: FONT_SIZE,
          textAlign: 'right',
          textAnchor: 'end',
          fill: lineColor,
        }}
      >
        {descentLabel}
      </text>
      <text
        x={bounds.paddingLeft - 6}
        y={posY + 4}
        style={{
          fontWeight: FONT_STYLE,
          fontFamily: FONT_FAMILY,
          fontSize: FONT_SIZE,
          textAlign: 'right',
          textAnchor: 'end',
          fill: lineColor,
        }}
      >
        {elevationLabel}
      </text>
      <text
        x={hoverX}
        y={bounds.paddingTop + bounds.innerHeight + bounds.paddingBottom - 3}
        style={{
          fontWeight: FONT_STYLE,
          fontFamily: FONT_FAMILY,
          fontSize: FONT_SIZE,
          textAlign: 'right',
          textAnchor: 'middle',
          fill: lineColor,
        }}
      >
        {distanceLabel}
      </text>
      <line
        x1={hoverX}
        x2={hoverX}
        y1={0}
        y2={bounds.paddingTop + bounds.innerHeight}
        stroke={lineColor}
        strokeWidth={1}
      />
      <line
        x1={bounds.paddingLeft}
        x2={bounds.paddingLeft + bounds.innerWidth}
        y1={posY}
        y2={posY}
        stroke={lineColor}
        strokeWidth={1}
      />
    </g>
  );
}
